import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

/**
 * Основная кнопка: 64px, r12, паддинги 22/20, зазор 10, подпись 14px Geist Mono
 * CAPS с трекингом −0.01em (DS Button, figma 1:2).
 *
 * Ховер только затемняет заливку — ни подъёма, ни роста тени, ни scale-up
 * плашки. Нажатие переиспользует цвет ховера; поверхность слегка уступает
 * (`.iec-press`), глиф пружинит. Цветового `:active` нет.
 */
// `not-disabled:hover:`, а НЕ `enabled:hover:`: псевдокласс `:enabled`
// существует только у form-элементов, и те же классы, надетые на `<a>`
// («Написать в поддержку», «Открыть сервис»), оставались вовсе без ховера.
// `:not(:disabled)` истинно и для ссылки, и для включённой кнопки.
const buttonVariants = cva(
	[
		"t-btn relative inline-flex min-h-16 items-center justify-center gap-2.5 rounded-card",
		// Подпись переносится, а высота растёт: `h-16` + `whitespace-nowrap`
		// означало, что немецкая или венгерская подпись вылезает за плашку.
		// `min-h-16` оставляет однострочную кнопку ровно 64px, как в макете, и
		// даёт вторую строку только тем языкам, которым она нужна.
		// `break-words` — на случай одного длинного слова без пробелов
		// («Benachrichtigungseinstellungen»): переносить его не по чему.
		// `hyphens-auto` работает, потому что <html lang> проставляется (i18n §8).
		"px-5 py-5.5 text-center text-fg",
		"break-words hyphens-auto",
		"iec-press",
		"cursor-pointer focus-ring",
		"disabled:cursor-not-allowed disabled:bg-btn-disabled disabled:text-fg-disabled disabled:shadow-none",
	],
	{
		variants: {
			variant: {
				primary: "bg-brand not-disabled:hover:bg-brand-hover",
				// Заливка second — compoundVariants: на белом `#f1f2f4`, на сером
				// холодная пара. `#fffffb` сливался с карточкой.
				second: "",
				ghost: "bg-transparent not-disabled:hover:bg-black/4",
			},
			/**
			 * `false` — кнопка на белой карточке: `bg-btn-secondary` (`#f1f2f4`).
			 * `true` — на sunken/page: холодная серая, иначе растворяется в плашке.
			 */
			onGrey: { true: "", false: "" },
			fullWidth: { true: "w-full", false: "" },
		},
		compoundVariants: [
			{
				variant: "second",
				onGrey: false,
				class: "bg-btn-secondary not-disabled:hover:bg-btn-secondary-hover",
			},
			{
				variant: "second",
				onGrey: true,
				class: "bg-btn-on-grey not-disabled:hover:bg-btn-on-grey-hover",
			},
		],
		defaultVariants: { variant: "primary", onGrey: false, fullWidth: false },
	},
);

export type ButtonState = "default" | "hover" | "disabled" | "loading";

export interface ButtonProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
		VariantProps<typeof buttonVariants> {
	/**
	 * Явное состояние. `hover` нужен только витринам состояний и скриншот-тестам —
	 * в живом интерфейсе ховер приходит от CSS, а не от пропа.
	 */
	state?: ButtonState;
	/**
	 * Удобная форма `state="loading"`. Держим оба входа: экраны обычно знают
	 * булев признак «мутация в полёте», а витрине состояний нужен явный `state`.
	 */
	loading?: boolean;
	/**
	 * Нативный проп рядом с `state="disabled"`: `state` — режим показа для
	 * витрины компонентов, а `disabled` — обычное «кнопка сейчас недоступна»,
	 * которое приходит из формы. Раньше он был вырезан из типа, и вызывающая
	 * сторона писала `state={busy ? "disabled" : "default"}` — состояние формы
	 * через проп внешнего вида.
	 */
	disabled?: boolean;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
	children?: ReactNode;
}

export function Button({
	variant,
	onGrey,
	fullWidth,
	state = "default",
	loading: loadingProp,
	disabled: disabledProp,
	iconLeft,
	iconRight,
	className,
	children,
	type = "button",
	...rest
}: ButtonProps) {
	const disabled = state === "disabled" || disabledProp === true;
	const loading = loadingProp === true || state === "loading";
	const forcedHover = state === "hover";

	return (
		<button
			type={type}
			disabled={disabled || loading}
			aria-busy={loading || undefined}
			className={cn(
				buttonVariants({ variant, onGrey, fullWidth }),
				// Форсированный ховер повторяет ровно тот же токен, что и :hover.
				forcedHover && variant !== "second" && variant !== "ghost" && "bg-brand-hover",
				forcedHover && variant === "second" && onGrey && "bg-btn-on-grey-hover",
				forcedHover && variant === "second" && !onGrey && "bg-btn-secondary-hover",
				forcedHover && variant === "ghost" && "bg-black/4",
				className,
			)}
			{...rest}
		>
			{loading ? <Spinner size={20} /> : iconLeft}
			<span>{children}</span>
			{loading ? null : iconRight}
		</button>
	);
}

export { buttonVariants };
